/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';

export type EntityTableCommandBarProps = TableCommandBarProps<EntityStore> & {
    /**
     * Determines whether the rename button is disabled or not.
     */
    isRenameEntityDisabled?: boolean;

    /**
     * Callback for creating an entity.
     */
    onCreateEntity: () => void;

    /**
     * Callback for adding a prebuilt entity.
     */
    onAddPrebuiltEntity: () => void;

    /**
     * Callback for adding a prebuilt domain entity.
     */
    onAddPrebuiltDomainEntity: () => void;

    /**
     * Callback for renaming an entity.
     */
    onRenameEntity: () => void;

    /**
     * Callback for deleting entities.
     */
    onDeleteEntities: () => void;

    /**
     * User role to determine the actions.
     */
    userRole: string;

    /**
     * User role to show in LuisTooltip message.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    createEntityButton: {
        id: 'entities.entityTableCommandBar.createEntityButton',
        defaultMessage: 'Create'
    },
    addPrebuiltEntityButton: {
        id: 'entities.entityTableCommandBar.addPrebuiltEntityButton',
        defaultMessage: 'Add prebuilt entity'
    },
    addPrebuiltDomainEntityButton: {
        id: 'entities.entityTableCommandBar.addPrebuiltDomainEntityButton',
        defaultMessage: 'Add prebuilt domain entity'
    },
    renameEntityButton: {
        id: 'entities.entityTableCommandBar.renameEntityButton',
        defaultMessage: 'Rename'
    },
    deleteEntityButton: {
        id: 'entities.entityTableCommandBar.deleteEntityButton',
        defaultMessage: 'Delete'
    }
});

/**
 * A command bar for the entities list table.
 *
 * @param props The properties for the  entities list table command bar.
 */
export const EntityTableCommandBar = (props: EntityTableCommandBarProps) => {
    const { selectedItems = [], query = '', onQueryChange, isRenameEntityDisabled, userRole, displayedUserRole } = props;
    const { localize } = useLocalization();

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    const commands: ICommandBarItemProps[] = [
        {
            key: 'create',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="createButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onCreateEntity}
                            iconProps={{ iconName: 'Add' }}
                            data-automation-id="createButton"
                            disabled={!canWrite || item.disabled}
                            text={localize(messages.createEntityButton)}
                            name={localize(messages.createEntityButton)}
                            ariaLabel={localize(messages.createEntityButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'addPrebuilt',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="addPrebuiltButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onAddPrebuiltEntity}
                            iconProps={{ iconName: 'Package' }}
                            disabled={!canWrite || item.disabled}
                            data-automation-id="addPrebuiltButton"
                            text={localize(messages.addPrebuiltEntityButton)}
                            name={localize(messages.addPrebuiltEntityButton)}
                            ariaLabel={localize(messages.addPrebuiltEntityButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'addPrebuiltDomain',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="addPrebuiltDomainButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canWrite || item.disabled}
                            onClick={props.onAddPrebuiltDomainEntity}
                            iconProps={{ iconName: 'WebComponents' }}
                            data-automation-id="addPrebuiltDomainButton"
                            text={localize(messages.addPrebuiltDomainEntityButton)}
                            name={localize(messages.addPrebuiltDomainEntityButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            ariaLabel={localize(messages.addPrebuiltDomainEntityButton)}
                        />
                    }
                />
            )
        },
        {
            key: 'rename',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="renameButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onRenameEntity}
                            data-automation-id="renameButton"
                            iconProps={{ iconName: 'Rename' }}
                            disabled={!canWrite || item.disabled}
                            text={localize(messages.renameEntityButton)}
                            name={localize(messages.renameEntityButton)}
                            ariaLabel={localize(messages.renameEntityButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canDeleteData || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onDeleteEntities}
                            data-automation-id="deleteButton"
                            iconProps={{ iconName: 'Delete' }}
                            disabled={!canDeleteData || item.disabled}
                            text={localize(messages.deleteEntityButton)}
                            name={localize(messages.deleteEntityButton)}
                            ariaLabel={localize(messages.deleteEntityButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        }
    ];

    const updatedCommands: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['create', 'addPrebuilt', 'addPrebuiltDomain'],
        ['rename', 'delete'],
        ['delete'],
        selectedItems.length
    );

    if (selectedItems.length === 1 && isRenameEntityDisabled) {
        updatedCommands.find(c => c.key === 'rename').disabled = true;
    }

    return <LuisCommandBar items={updatedCommands} includeSearchBar searchBarAlwaysExpanded query={query} onQueryChange={onQueryChange} />;
};
